import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Button,
  Switch,
  Divider,
  Typography,
  message,
  Tooltip,
  Empty,
} from "antd";
import {
  ShopOutlined,
  PlusOutlined,
  DeleteOutlined,
  TableOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ReservationSettings, RestaurantTable } from "../../../../utils/api/reservationsAPI";
import "./ReservationSettingsModal.scss";

const { Title, Text } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
  settings: ReservationSettings | null;
  onSave: (data: Partial<ReservationSettings>) => Promise<void>;
  saving: boolean;
};

const ReservationSettingsModal: React.FC<Props> = ({
  open,
  onClose,
  settings,
  onSave,
  saving,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [tables, setTables] = useState<Omit<RestaurantTable, "_id">[]>([]);

  useEffect(() => {
    if (open && settings) {
      form.setFieldsValue({
        restaurantName: settings.restaurantName || "",
        requireConfirmation: settings.requireConfirmation ?? false,
        openingTime: settings.openingTime || "10:00",
        closingTime: settings.closingTime || "23:00",
        slotDurationMinutes: settings.slotDurationMinutes || 30,
        maxGuestsPerReservation: settings.maxGuestsPerReservation || 20,
        minNoticeHours: settings.minNoticeHours || 1,
        maxAdvanceBookingDays: settings.maxAdvanceBookingDays || 60,
      });
      // Deep-copy tables so edits don't mutate settings directly
      setTables((settings.tables || []).map((t) => ({
        name: t.name,
        capacity: t.capacity,
        isActive: t.isActive,
      })));
    }
  }, [open, settings]);

  const addTable = () => {
    setTables((prev) => [...prev, { name: `Table ${prev.length + 1}`, capacity: 4, isActive: true }]);
  };

  const updateTable = (idx: number, field: keyof Omit<RestaurantTable, "_id">, value: any) => {
    setTables((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };

  const removeTable = (idx: number) => {
    setTables((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      await onSave({ ...values, tables });
      message.success(t("Settings saved"));
    } catch (err: any) {
      if (err?.errorFields) return; // validation error, antd handles display
      message.error(t("Failed to save settings"));
    }
  };

  const totalCapacity = tables
    .filter((t) => t.isActive)
    .reduce((sum, t) => sum + (t.capacity || 0), 0);

  return (
    <Modal
      title={
        <div className="rsm-title">
          <ShopOutlined />
          <span>{t("Restaurant Settings")}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={560}
      className="standardHeader standardFooter reservation-settings-modal"
      footer={
        <div className="rsm-footer">
          <Button onClick={onClose}>{t("Cancel")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            {t("Save Settings")}
          </Button>
        </div>
      }
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark={false} className="rsm-form">
        {/* Restaurant name */}
        <Form.Item name="restaurantName" label={t("Restaurant Name")}>
          <Input
            prefix={<ShopOutlined />}
            placeholder={t("My Restaurant")}
            size="large"
          />
        </Form.Item>

        <Divider className="rsm-divider" />

        {/* Tables section */}
        <div className="rsm-section-header">
          <div>
            <Title level={5} className="rsm-section-title">{t("Tables")}</Title>
            {tables.length > 0 && (
              <Text type="secondary" className="rsm-section-sub">
                {tables.filter((t) => t.isActive).length} {t("active")} · {totalCapacity} {t("total seats")}
              </Text>
            )}
          </div>
          <Button icon={<PlusOutlined />} onClick={addTable} size="small">
            {t("Add Table")}
          </Button>
        </div>

        {tables.length === 0 ? (
          <div className="rsm-empty-tables">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  {t("No tables configured.")}{" "}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("Capacity will be based on max guests per slot setting.")}
                  </Text>
                </span>
              }
            />
          </div>
        ) : (
          <div className="rsm-tables">
            {tables.map((table, idx) => (
              <div
                key={idx}
                className={`rsm-table-row${!table.isActive ? " rsm-table-row--inactive" : ""}`}
              >
                <Switch
                  size="small"
                  checked={table.isActive}
                  onChange={(v) => updateTable(idx, "isActive", v)}
                  className="rsm-table-switch"
                />
                <Input
                  value={table.name}
                  onChange={(e) => updateTable(idx, "name", e.target.value)}
                  placeholder={`Table ${idx + 1}`}
                  className="rsm-table-name"
                  size="middle"
                />
                <div className="rsm-table-capacity-wrap">
                  <InputNumber
                    value={table.capacity}
                    onChange={(v) => updateTable(idx, "capacity", v || 1)}
                    min={1}
                    max={50}
                    size="middle"
                    className="rsm-table-capacity-input"
                  />
                  <Text type="secondary" className="rsm-table-seats-label">
                    {t("seats")}
                  </Text>
                </div>
                <Tooltip title={t("Remove table")}>
                  <Button
                    icon={<DeleteOutlined />}
                    size="small"
                    danger
                    type="text"
                    onClick={() => removeTable(idx)}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        )}

        <Divider className="rsm-divider" />

        {/* Booking rules */}
        <Title level={5} className="rsm-section-title">{t("Booking Rules")}</Title>

        <div className="rsm-rules-grid">
          <Form.Item name="openingTime" label={t("Opens at")}>
            <Input size="large" placeholder="10:00" />
          </Form.Item>
          <Form.Item name="closingTime" label={t("Closes at")}>
            <Input size="large" placeholder="23:00" />
          </Form.Item>
          <Form.Item name="minNoticeHours" label={t("Min notice (hours)")}>
            <InputNumber min={0} max={72} size="large" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="maxAdvanceBookingDays" label={t("Book up to (days)")}>
            <InputNumber min={1} max={365} size="large" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="maxGuestsPerReservation" label={t("Max guests/reservation")}>
            <InputNumber min={1} max={200} size="large" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="slotDurationMinutes" label={t("Slot duration (min)")}>
            <InputNumber min={15} max={120} step={15} size="large" style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <Form.Item
          name="requireConfirmation"
          label={t("Require manual confirmation for public bookings")}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ReservationSettingsModal;
